<script lang="ts">
  import type { Snippet } from "svelte";
  import type { HTMLDialogAttributes } from "svelte/elements";
  import { twMerge } from "tailwind-merge";
  import { CLOSE_ANIMATION_MS } from "./animation";

  interface Props extends Omit<HTMLDialogAttributes, "open" | "title" | "children" | "onclose"> {
    /** Whether it's showing. Closing it any way (Escape, ✕, a `method="dialog"` form) sets it false. */
    open?: boolean;
    /**
     * `dialog`: a small box with a title and buttons, for alerts and confirmations.
     * `sheet`: full screen on phones and a large box elsewhere; lay it out with SheetHeader,
     * SheetBody and SheetBack.
     */
    variant?: "dialog" | "sheet";
    title?: string;
    /** Show the ✕ button (dialogs). */
    closable?: boolean;
    /** Classes for the box, merged over the variant's. */
    boxClass?: string;
    /** Called when it closes, however it closes. */
    onclose?: () => void;
    /** Called once the closing animation is over, when it can be unmounted. */
    onclosed?: () => void;
    children: Snippet;
    /** The dialog's buttons. */
    actions?: Snippet;
  }

  let {
    open = $bindable(false),
    variant = "dialog",
    title,
    closable = false,
    boxClass = "",
    onclose,
    onclosed,
    children,
    actions,
    ...rest
  }: Props = $props();

  let dialog: HTMLDialogElement;

  $effect(() => {
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  });

  function closed() {
    open = false;
    onclose?.();
    if (onclosed) {
      setTimeout(onclosed, CLOSE_ANIMATION_MS);
    }
  }

  const SHEET_BOX =
    "modal-box max-md:p-0 text-base-content max-md:w-full max-md:max-w-none max-md:rounded-none md:pt-0 max-md:bg-secondary bg-base-100 overflow-hidden max-md:transition-none grid grid-rows-[auto_1fr_auto] max-md:h-svh-w-fallback max-md:pt-[env(safe-area-inset-top)] outline-none";
</script>

<dialog
  bind:this={dialog}
  class={variant === "sheet" ? "modal scrollbar-none max-md:transition-none" : "modal"}
  onclose={closed}
  {...rest}
>
  {#if variant === "sheet"}
    <!-- Opening focuses the box, not its first button: Safari shows that button as pressed. -->
    <div class={twMerge(SHEET_BOX, boxClass)} tabindex="-1" autofocus>
      {@render children()}
    </div>
  {:else}
    <div class={twMerge("modal-box text-base-content", boxClass)}>
      <form method="dialog">
        <div class="head">
          {#if title}
            <p class="font-bold text-xl"><b>{title}</b></p>
          {/if}
          {#if closable}
            <button
              class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
              data-testid="close-modal">✕</button
            >
          {/if}
        </div>
      </form>
      <div>{@render children()}</div>
      <div class="modal-action">{@render actions?.()}</div>
    </div>
  {/if}
</dialog>

<style>
  .head {
    padding-bottom: 0.5em;
  }
</style>
